import { Dialog } from '@angular/cdk/dialog';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastService } from '@libs/shared-services';
import { authActions } from '@libs/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, finalize, shareReplay, tap } from 'rxjs';
import {
  EaglesoftPasswordModalComponent,
  EaglesoftPasswordModalData,
  EaglesoftPasswordModalResult,
} from './eaglesoft-password-modal.component';

@Injectable({ providedIn: 'root' })
export class EaglesoftPasswordPromptService {
  private readonly dialog = inject(Dialog);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private inFlight$: Observable<EaglesoftPasswordModalResult | undefined> | null = null;

  /**
   * Opens the password modal, or joins the one already open — every caller gets the same result.
   * Late callers must not get EMPTY: the interceptor holds requests open on this, and a completed
   * observable would drop them silently. A joining caller also gets the open dialog's copy, so a
   * replay joining a proactive prompt shows no data-loss line.
   */
  // eslint-disable-next-line rxjs/finnish -- `prompt` is the established public method name; callers depend on it, do not rename to `prompt$`.
  public prompt(siteId: string, warnAboutDataLoss = false): Observable<EaglesoftPasswordModalResult | undefined> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    const dialogRef = this.dialog.open<EaglesoftPasswordModalResult, EaglesoftPasswordModalData>(EaglesoftPasswordModalComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[505px]'],
      disableClose: true,
      data: { siteId, warnAboutDataLoss },
    });

    this.inFlight$ = dialogRef.closed.pipe(
      tap((result) => {
        if (result?.success) {
          this.store.dispatch(authActions.getEaglesoftCredentialStatuses());
          // One confirmation, however many callers share this dialog.
          this.toastService.success(this.translateService.instant('account_eaglesoft_password_update_success_toast'));
        }
      }),
      // Released on close, so a prompt raised meanwhile joins instead of stacking a second dialog.
      finalize(() => (this.inFlight$ = null)),
      // refCount false: the result survives for callers subscribing after it emitted.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.inFlight$;
  }
}
